// src/pages/Certificates.js
import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { getUserCertificates, getCertificate, deleteCertificate } from '../firebase/services';
import { generatePDF } from '../utils/pdfGenerator';
import { getUserLogo } from './Settings';
import StatusBadge from '../components/StatusBadge';

export default function Certificates() {
  const { id } = useParams();
  return id ? <CertificateDetail id={id} /> : <CertificateList />;
}

function CertificateList() {
  const { user } = useAuth();
  const { t } = useLang();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (user) getUserCertificates(user.uid).then(setCerts).finally(() => setLoading(false));
  }, [user]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('dashboard.deleteConfirm'))) return;
    await deleteCertificate(id);
    setCerts(cs => cs.filter(c => c.id !== id));
  };

  const handleDownload = (cert) => {
    if (cert.status !== 'approved') return;
    generatePDF({
      // new format
      contact: cert.contact, sample: cert.sample, analysis: cert.analysis,
      // legacy format fallback
      product: cert.product, specs: cert.specs, lab: cert.lab,
      certId: cert.certId,
      includeQR: cert.includeQR, includeLogo: cert.includeLogo, logoDataUrl: getUserLogo(),
      productLabelUrl: cert.productLabelUrl || '',
      doctorName: cert.doctorName || '',
      doctorTitle: cert.doctorTitle || '',
      doctorSignatureUrl: cert.doctorSignatureUrl || '',
      results: cert.results || null,
      charts: cert.charts || [],
      richText: cert.richText || null,
    }, cert.lang || 'en');
  };

  const filtered = certs.filter(c => {
    const nameNew = c.sample?.productName?.toLowerCase() || '';
    const nameOld = c.product?.chemName?.toLowerCase() || '';
    const matchSearch = !search ||
      nameNew.includes(search.toLowerCase()) ||
      nameOld.includes(search.toLowerCase()) ||
      c.certId?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || c.status === filter;
    return matchSearch && matchFilter;
  });

  const formatDate = (ts) => {
    if (!ts?.seconds) return '—';
    return new Date(ts.seconds * 1000).toLocaleDateString();
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>{t('nav.certificates')}</h1>
        <Link to="/generate" style={s.generateBtn}>{t('dashboard.generate')}</Link>
      </div>

      <div style={s.filters}>
        <input style={s.search} placeholder={t('common.search')} value={search} onChange={e => setSearch(e.target.value)} />
        <div style={s.filterBtns}>
          {[
            ['all', 'All'],
            ['pending', t('coa.pending.statusPending')],
            ['approved', t('coa.pending.statusApproved')],
            ['rejected', t('coa.pending.statusRejected')],
          ].map(([f, label]) => (
            <button key={f} style={{ ...s.filterBtn, ...(filter === f ? s.filterActive : {}) }} onClick={() => setFilter(f)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div style={s.empty}>{t('common.loading')}</div> : filtered.length === 0 ? (
        <div style={s.emptyBox}>
          <p style={s.emptyText}>{t('dashboard.noData')}</p>
          <Link to="/generate" style={s.generateBtn}>{t('dashboard.generate')}</Link>
        </div>
      ) : (
        <div style={s.certGrid}>
          {filtered.map(cert => (
            <div key={cert.id} style={s.certCard}>
              <div style={s.certCardHeader}>
                <span style={s.certIdBadge}>{cert.certId || cert.id.slice(0, 12)}</span>
                <StatusBadge status={cert.status || 'pending'} t={t} size="small" />
              </div>
              <h3 style={s.certName}>{cert.sample?.productName || cert.product?.chemName || '—'}</h3>
              <div style={s.certMeta}>
                <span>{t('dashboard.batch')}: {cert.sample?.lot || cert.product?.batch || '—'}</span>
                <span>{formatDate(cert.createdAt)}</span>
              </div>

              {cert.status === 'rejected' && cert.rejectionReason && (
                <div style={s.rejectionBox}>
                  <strong>{t('coa.pending.rejectionReason')}:</strong> {cert.rejectionReason}
                </div>
              )}
              {cert.status === 'pending' && (
                <div style={s.waitingBox}>{t('coa.pending.waitingMsg')}</div>
              )}

              <div style={s.certActions}>
                <Link to={`/certificates/${cert.id}`} style={s.viewBtn}>{t('common.view')}</Link>
                {cert.status === 'approved' ? (
                  <button style={s.dlBtn} onClick={() => handleDownload(cert)}>{t('common.download')}</button>
                ) : (
                  <button style={s.dlBtnDisabled} disabled title={t('coa.pending.notDownloadable')}>
                    {t('common.download')}
                  </button>
                )}
                <button style={s.deleteBtn} onClick={() => handleDelete(cert.id)}>{t('common.delete')}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CertificateDetail({ id }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCertificate(id).then(c => { setCert(c); setLoading(false); });
  }, [id]);

  if (loading) return <div style={{ ...s.page, color: '#fff', paddingTop: 100 }}>{t('common.loading')}</div>;
  if (!cert) return <div style={{ ...s.page, color: '#fff', paddingTop: 100 }}>Certificate not found.</div>;

  const formatDate = (ts) => {
    if (!ts?.seconds) return '—';
    return new Date(ts.seconds * 1000).toLocaleDateString();
  };

  const handleDownload = () => {
    if (cert.status !== 'approved') return;
    generatePDF({
      // new format
      contact: cert.contact, sample: cert.sample, analysis: cert.analysis,
      // legacy format fallback
      product: cert.product, specs: cert.specs, lab: cert.lab,
      certId: cert.certId,
      includeQR: cert.includeQR, includeLogo: cert.includeLogo, logoDataUrl: getUserLogo(),
      productLabelUrl: cert.productLabelUrl || '',
      doctorName: cert.doctorName || '',
      doctorTitle: cert.doctorTitle || '',
      doctorSignatureUrl: cert.doctorSignatureUrl || '',
      results: cert.results || null,
      charts: cert.charts || [],
      richText: cert.richText || null,
    }, cert.lang || lang);
  };

  return (
    <div style={s.page}>
      <div style={s.detailHeader}>
        <button style={s.backBtn} onClick={() => navigate('/certificates')}>← {t('coa.preview.back')}</button>
        {cert.status === 'approved' ? (
          <button style={s.dlBtnDetail} onClick={handleDownload}>⬇ {t('coa.preview.downloadPDF')}</button>
        ) : (
          <button style={s.dlBtnDetailDisabled} disabled>🔒 {t('coa.pending.notDownloadable')}</button>
        )}
      </div>

      <div style={s.detailCard}>
        <div style={s.detailTop}>
          <div>
            <div style={s.certIdMonospace}>{cert.certId}</div>
            <h1 style={s.detailProduct}>{cert.sample?.productName || cert.product?.chemName || '—'}</h1>
          </div>
          <StatusBadge status={cert.status || 'pending'} t={t} />
        </div>

        {/* Status-specific banners */}
        {cert.status === 'pending' && (
          <div style={s.bannerPending}>
            ⏳ {t('coa.pending.waitingMsg')}
          </div>
        )}
        {cert.status === 'rejected' && (
          <div style={s.bannerRejected}>
            <strong>✕ {t('coa.pending.rejectionReason')}:</strong>
            <p style={{ margin: '6px 0 0' }}>{cert.rejectionReason || '—'}</p>
          </div>
        )}
        {cert.status === 'approved' && cert.reviewedBy && (
          <div style={s.bannerApproved}>
            ✓ {t('coa.pending.reviewedBy')}: {cert.reviewedBy}
          </div>
        )}

        {/* ── New format: contact + sample + analysis ── */}
        {cert.sample?.productName ? (
          <>
            {/* Contact info */}
            {cert.contact?.companyName && (
              <div style={s.sectionBlock}>
                <h2 style={s.specsTitle}>Contact Information</h2>
                <div style={s.detailGrid}>
                  {[
                    ['Company', cert.contact.companyName],
                    ['Address', cert.contact.address],
                    ['Bill To', cert.contact.billTo],
                    ['Purchase Order', cert.contact.purchaseOrder],
                    ['Request Date', cert.contact.requestDate],
                  ].filter(([,v]) => v).map(([lbl, val]) => (
                    <div key={lbl}>
                      <div style={s.detailLabel}>{lbl}</div>
                      <div style={s.detailValue}>{val}</div>
                    </div>
                  ))}
                  <div>
                    <div style={s.detailLabel}>{t('dashboard.date')}</div>
                    <div style={s.detailValue}>{formatDate(cert.createdAt)}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Sample info */}
            <div style={s.sectionBlock}>
              <h2 style={s.specsTitle}>Sample Information</h2>
              <div style={s.detailGrid}>
                {[
                  ['Product Name', cert.sample.productName],
                  ['Lot / Batch', cert.sample.lot],
                  ['Presentation', cert.sample.presentation],
                  ['Sample Qty', cert.sample.sampleQty],
                  ['Active Ingredient', cert.sample.activeIngredient],
                  ['Dose / Concentration', cert.sample.doseConcentration],
                  ['Mfg. Date', cert.sample.mfgDate],
                  ['Exp. Date', cert.sample.expDate],
                  ['Supplier', cert.sample.supplier],
                  ['Manufacturer', cert.sample.manufacturer],
                  ['Sanitary Registry', cert.sample.sanitaryRegistry],
                  ['Pharmaceutical Form', cert.sample.pharmaceuticalForm],
                  ['Line', cert.sample.line],
                  ['Category', cert.sample.category],
                  ['Sample Type', (cert.sample.sampleType || []).join(', ')],
                  ['Methodology', (cert.sample.methodology || []).join(', ')],
                  ['Storage', cert.sample.storageCondition],
                ].filter(([,v]) => v).map(([lbl, val]) => (
                  <div key={lbl}>
                    <div style={s.detailLabel}>{lbl}</div>
                    <div style={s.detailValue}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Product label image */}
            {cert.productLabelUrl && (
              <div style={s.sectionBlock}>
                <h2 style={s.specsTitle}>🏷️ Product Label</h2>
                <a href={cert.productLabelUrl} target="_blank" rel="noopener noreferrer">
                  <img src={cert.productLabelUrl} alt="Product label" style={s.labelImg} />
                </a>
              </div>
            )}

            {/* Analysis requested */}
            {cert.analysis && (
              <div style={s.sectionBlock}>
                <h2 style={s.specsTitle}>Analysis Requested</h2>
                <div style={{ overflowX: 'auto' }}>
                  <table style={s.specsTable}>
                    <thead>
                      <tr style={{ background: 'rgba(30,94,255,0.1)' }}>
                        <th style={s.specsHead}>Parameter</th>
                        <th style={s.specsHead}>Specification</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(cert.analysis).filter(([k,v]) => k !== 'observations' && v?.selected).map(([k, v]) => (
                        <tr key={k} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={s.specsTd}>{k}</td>
                          <td style={s.specsTd}>{v.spec || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {cert.analysis.observations && (
                  <div style={{ marginTop: 12, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                    <strong>Observations:</strong> {cert.analysis.observations}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* ── Legacy format: product + specs + lab ── */}
            <div style={s.detailGrid}>
              {[['casNumber', cert.product?.casNumber], ['formula', cert.product?.formula], ['weight', cert.product?.weight], ['batch', cert.product?.batch], ['mfgDate', cert.product?.mfgDate], ['expDate', cert.product?.expDate], ['country', cert.product?.country]].map(([k, v]) => (
                <div key={k}>
                  <div style={s.detailLabel}>{t(`coa.product.${k}`)}</div>
                  <div style={s.detailValue}>{v || '—'}</div>
                </div>
              ))}
              <div>
                <div style={s.detailLabel}>{t('dashboard.date')}</div>
                <div style={s.detailValue}>{formatDate(cert.createdAt)}</div>
              </div>
            </div>

            <h2 style={s.specsTitle}>{t('coa.steps.specs')}</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={s.specsTable}>
                <thead>
                  <tr style={{ background: 'rgba(30,94,255,0.1)' }}>
                    {['parameter', 'specification', 'result', 'unit', 'method'].map(col => (
                      <th key={col} style={s.specsHead}>{t(`coa.specs.${col}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(cert.specs || []).map((sp, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      {['parameter', 'specification', 'result', 'unit', 'method'].map(col => (
                        <td key={col} style={s.specsTd}>{sp[col] || '—'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {cert.lab?.labName && (
              <div style={s.labSection}>
                <h2 style={s.specsTitle}>{t('coa.steps.lab')}</h2>
                <div style={s.detailGrid}>
                  {[['labName', cert.lab.labName], ['analyst', cert.lab.analyst], ['approvedBy', cert.lab.approvedBy], ['approvalDate', cert.lab.approvalDate]].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}>
                      <div style={s.detailLabel}>{t(`coa.lab.${k}`)}</div>
                      <div style={s.detailValue}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#070F2B', padding: '84px 24px 48px', maxWidth: 1200, margin: '0 auto', fontFamily: "'Inter', sans-serif", color: '#fff' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 },
  title: { fontSize: 28, fontWeight: 800, color: '#fff', margin: 0 },
  generateBtn: { background: 'linear-gradient(135deg, #1E5EFF, #0A3DD4)', color: '#fff', textDecoration: 'none', padding: '11px 22px', borderRadius: 12, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', display: 'inline-block' },
  filters: { display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' },
  search: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 14px', color: '#fff', fontSize: 14, outline: 'none', width: 240 },
  filterBtns: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  filterBtn: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  filterActive: { background: 'rgba(30,94,255,0.2)', border: '1px solid rgba(30,94,255,0.4)', color: '#fff' },
  certGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: 16 },
  certCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: 20 },
  certCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  certIdBadge: { fontFamily: 'monospace', fontSize: 11, color: '#4A9EFF', background: 'rgba(74,158,255,0.1)', padding: '3px 8px', borderRadius: 6 },
  certName: { fontSize: 18, fontWeight: 700, color: '#fff', margin: '0 0 8px' },
  certMeta: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'rgba(255,255,255,0.45)', marginBottom: 12 },
  rejectionBox: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#FCA5A5', marginBottom: 12, lineHeight: 1.5 },
  waitingBox: { background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#FBBF24', marginBottom: 12 },
  certActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  viewBtn: { color: '#4A9EFF', textDecoration: 'none', fontSize: 13, fontWeight: 600, padding: '6px 12px', border: '1px solid rgba(74,158,255,0.3)', borderRadius: 8 },
  dlBtn: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, fontWeight: 600 },
  dlBtnDisabled: { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.25)', fontSize: 13, cursor: 'not-allowed', padding: '6px 12px', borderRadius: 8, fontWeight: 600 },
  deleteBtn: { background: 'none', border: '1px solid rgba(239,68,68,0.3)', color: '#F87171', fontSize: 13, cursor: 'pointer', padding: '6px 12px', borderRadius: 8 },
  empty: { textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: 60 },
  emptyBox: { textAlign: 'center', padding: 60 },
  emptyText: { color: 'rgba(255,255,255,0.5)', fontSize: 15, marginBottom: 20 },
  // Detail
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  backBtn: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', padding: '10px 18px', borderRadius: 10, fontSize: 14, cursor: 'pointer', fontWeight: 600 },
  dlBtnDetail: { background: 'linear-gradient(135deg, #1E5EFF, #0A3DD4)', border: 'none', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 14, cursor: 'pointer', fontWeight: 700 },
  dlBtnDetailDisabled: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.35)', padding: '10px 20px', borderRadius: 10, fontSize: 14, cursor: 'not-allowed', fontWeight: 600 },
  detailCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 20, padding: '28px 28px' },
  detailTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  certIdMonospace: { fontFamily: 'monospace', fontSize: 13, color: '#4A9EFF', marginBottom: 6 },
  detailProduct: { fontSize: 26, fontWeight: 800, color: '#fff', margin: 0 },
  bannerPending:  { background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 12, padding: '14px 18px', color: '#FBBF24', fontSize: 14, marginBottom: 24 },
  bannerRejected: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 12, padding: '14px 18px', color: '#FCA5A5', fontSize: 14, marginBottom: 24 },
  bannerApproved: { background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 12, padding: '14px 18px', color: '#6EE7B7', fontSize: 14, marginBottom: 24 },
  detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))', gap: 20, marginBottom: 28 },
  detailLabel: { fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 },
  detailValue: { fontSize: 15, color: '#fff', fontWeight: 500 },
  specsTitle: { fontSize: 18, fontWeight: 700, color: '#fff', margin: '0 0 16px' },
  specsTable: { width: '100%', borderCollapse: 'collapse', minWidth: 500 },
  specsHead: { padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  specsTd: { padding: '11px 14px', fontSize: 14, color: 'rgba(255,255,255,0.75)' },
  labSection: { marginTop: 28 },
  sectionBlock: { marginBottom: 28 },
  labelImg: { maxWidth: '100%', maxHeight: 260, borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)', cursor: 'zoom-in', display: 'block' },
};
